import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { AlertTriangle, BookOpen, RefreshCw, Search, Upload } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getErrorMessage } from "../../api/client";
import {
  EsitoRitiro,
  RigaSituazioneRitiri,
  SituazioneRitiri,
  datiVecchi,
  formattaDataAznext,
  istanteDataAznext,
  situazioneRitiri,
} from "../../api/ritiri";
import { CardRitiri, KpiRitiri, PillolaRitiri, PulsanteRitiri, SchedeRitiri, TestataRitiri } from "../../components/ritiri/TestataRitiri";
import { AiutoRitiri } from "../../components/ritiri/AiutoRitiri";
import { FinestraRitiri } from "../../components/ritiri/FinestraRitiri";
import { BadgeEsito, BarraRiempimento, ChipEtichette, giornoBreve } from "./presentazione";
import { DettaglioCassaforte, ID_TITOLO_DETTAGLIO } from "./DettaglioCassaforte";
import { ID_TITOLO_LEGENDA, LegendaRitiri } from "./LegendaRitiri";
import { RegoleRitiri } from "./RegoleRitiri";
import { ID_TITOLO_IMPOSTAZIONI, ImpostazioniCassaforte } from "./ImpostazioniCassaforte";
import { RichiesteRitiri } from "./RichiesteRitiri";
import { BARRE, DATE } from "./testiLegenda";

/**
 * RITIRI VALORI — dashboard (docs/specs/ritiri-valori.md, § 7; aspetto:
 * docs/specs/ritiri-valori-riferimento-grafico.md).
 *
 * Mostra quello che calcola il backend (§ 0.6): esiti, conteggi, scadenze,
 * percentuali e ordine dell'elenco arrivano da GET /api/ritiri/situazione.
 * La pagina filtra e conta i minuti che mancano alla scadenza; non ricalcola
 * nessuna regola. Quando la scadenza passa rilegge la situazione, e il D
 * successivo lo dice l'API.
 *
 * Dal dettaglio si preparano le richieste con le bozze email e si aprono le
 * impostazioni della cassaforte (v1.14); la scheda "Richieste" ha l'elenco e
 * lo storico. Le bozze le spedisce una persona, da Outlook o con il tasto
 * Invia della richiesta (§ 8 bis): mai l'app da sola.
 */

const SCHEDE = ["Situazione", "Richieste", "Regole"] as const;
type Scheda = (typeof SCHEDE)[number];

/** Riquadri della testata (§ 7 punto 1). */
const KPI: { esito: EsitoRitiro; etichetta: string; caldo?: boolean }[] = [
  { esito: "rischio_blocco", etichetta: "Rischio blocco", caldo: true },
  { esito: "richiedi", etichetta: "Richiedi" },
  { esito: "da_ricontrollare", etichetta: "Da ricontrollare" },
  { esito: "consigliato", etichetta: "Consigliato" },
  { esito: "richiesta_inviata", etichetta: "Richieste inviate" },
];

const SENZA_TRASPORTATORE = "__nessuno__";

// ----------------------------------------------------------------------------
// Conto alla rovescia
// ----------------------------------------------------------------------------

/**
 * Istante corrente (`Date.now()`), aggiornato a ogni cambio di minuto. I minuti
 * di Roma e quelli UTC cambiano insieme: i fusi spostano l'ora di ore intere.
 */
function useAdesso(): number {
  const [ora, setOra] = useState(Date.now);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const giro = () => {
      setOra(Date.now());
      timer = setTimeout(giro, 60_000 - (Date.now() % 60_000) + 20);
    };
    timer = setTimeout(giro, 60_000 - (Date.now() % 60_000) + 20);
    return () => clearTimeout(timer);
  }, []);
  return ora;
}

/**
 * Tempo rimasto come `hh:mm` (le ore possono superare 24 nel fine settimana).
 * I minuti si arrotondano per eccesso: con 30 secondi rimasti si legge 00:01,
 * e un giro del timer arrivato qualche istante dopo il cambio di minuto (o in
 * ritardo, come nelle schede in background) non fa perdere un minuto intero.
 */
function hhmm(ms: number): string {
  const minuti = Math.ceil(ms / 60_000);
  return `${String(Math.floor(minuti / 60)).padStart(2, "0")}:${String(minuti % 60).padStart(2, "0")}`;
}

// ----------------------------------------------------------------------------
// Schermo largo o telefono: sotto `lg` dettaglio e legenda sono a tutta pagina
// ----------------------------------------------------------------------------

/** Lo stesso breakpoint `lg` di Tailwind (1024px). */
const QUERY_LARGO = "(min-width: 1024px)";

function useSchermoLargo(): boolean {
  const [largo, setLargo] = useState(() => window.matchMedia(QUERY_LARGO).matches);
  useEffect(() => {
    const mq = window.matchMedia(QUERY_LARGO);
    const cambio = () => setLargo(mq.matches);
    cambio();
    mq.addEventListener("change", cambio);
    return () => mq.removeEventListener("change", cambio);
  }, []);
  return largo;
}

// ----------------------------------------------------------------------------
// Pagina
// ----------------------------------------------------------------------------

export default function RitiriValori() {
  const { hasPermission } = useAuth();
  const [scheda, setScheda] = useState<Scheda>("Situazione");
  const [situazione, setSituazione] = useState<SituazioneRitiri | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCaricamento, setInCaricamento] = useState(true);
  // Cresce a ogni lettura riuscita: il dettaglio aperto si rilegge con la situazione.
  const [versione, setVersione] = useState(0);
  const [selezionata, setSelezionata] = useState<string | null>(null);
  const [filtroEsito, setFiltroEsito] = useState<EsitoRitiro | "">("");
  const [filtroTrasportatore, setFiltroTrasportatore] = useState("");
  const [soloNonEffettuati, setSoloNonEffettuati] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const richiesta = useRef(0);

  const carica = useCallback(async () => {
    const mia = ++richiesta.current;
    setInCaricamento(true);
    try {
      const dati = await situazioneRitiri();
      if (mia !== richiesta.current) return;
      setSituazione(dati);
      setErrore(null);
      setVersione((v) => v + 1);
    } catch (err) {
      if (mia !== richiesta.current) return;
      setErrore(getErrorMessage(err, "Impossibile caricare la situazione dei ritiri."));
    } finally {
      if (mia === richiesta.current) setInCaricamento(false);
    }
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  // Scadenza delle richieste: quando passa si rilegge la situazione, una volta
  // per scadenza. Il nuovo D lo decide il backend.
  // Durata tra istanti reali: con il cambio dell'ora in mezzo, due orari da
  // orologio sbaglierebbero di un'ora (vedi `istanteDataAznext`).
  const ora = useAdesso();
  const scadenza = situazione?.calendario_base.scadenza ?? null;
  const istanteScadenza = scadenza ? istanteDataAznext(scadenza) : null;
  const mancano = istanteScadenza === null ? null : istanteScadenza - ora;
  const rilettaPer = useRef<string | null>(null);
  useEffect(() => {
    if (scadenza && mancano !== null && mancano <= 0 && rilettaPer.current !== scadenza) {
      rilettaPer.current = scadenza;
      carica();
    }
  }, [scadenza, mancano, carica]);

  // Sopra `lg` il dettaglio è una colonna; sotto è una finestra modale a tutta
  // pagina (`FinestraRitiri`: fuoco contenuto, sfondo inerte, Esc, fuoco
  // restituito). Alla chiusura il fuoco torna sul pulsante della cassaforte.
  //
  // MAI DUE FINESTRE INSIEME. Legenda e impostazioni sono l'unica finestra in
  // primo piano: finché una è aperta il dettaglio in finestra non si monta,
  // anche se la pagina scende sotto `lg` (rotazione del tablet). Chiusa quella,
  // se la cassaforte è ancora selezionata il dettaglio compare. Due
  // `FinestraRitiri` insieme si renderebbero inerti a vicenda e un Esc le
  // chiuderebbe tutte e due (segnalazione Codex sulla #203).
  const largo = useSchermoLargo();
  const [finestra, setFinestra] = useState<"legenda" | "impostazioni" | null>(null);
  const chiudiFinestra = useCallback(() => setFinestra(null), []);
  const apriImpostazioni = useCallback(() => setFinestra("impostazioni"), []);
  // Il dettaglio appartiene alla scheda Situazione: sulle altre schede non
  // compare in finestra, nemmeno se la pagina scende sotto `lg`.
  const modale = selezionata !== null && !largo && finestra === null && scheda === "Situazione";

  const daRiprendere = useRef<string | null>(null);
  const chiudiDettaglio = useCallback(() => {
    daRiprendere.current = selezionata;
    setSelezionata(null);
  }, [selezionata]);
  useEffect(() => {
    if (selezionata !== null || !daRiprendere.current) return;
    const id = daRiprendere.current;
    daRiprendere.current = null;
    document.querySelector<HTMLButtonElement>(`li[data-id-macchina="${CSS.escape(id)}"] button[aria-pressed]`)?.focus();
  }, [selezionata]);

  // La colonna del dettaglio (schermo largo) si chiude con Esc; sotto `lg` e
  // con un'altra finestra aperta l'Esc lo gestisce la finestra.
  useEffect(() => {
    if (!selezionata || !largo || finestra !== null) return;
    const tasto = (e: KeyboardEvent) => e.key === "Escape" && chiudiDettaglio();
    window.addEventListener("keydown", tasto);
    return () => window.removeEventListener("keydown", tasto);
  }, [selezionata, largo, finestra, chiudiDettaglio]);

  const righe = situazione?.righe ?? [];
  const trasportatori = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of righe) for (const t of r.trasportatori) m.set(t.id, t.nome);
    return [...m].sort((a, b) => a[1].localeCompare(b[1], "it"));
  }, [righe]);

  // Filtri: l'ordine resta quello dell'API (gravità, poi p2 decrescente).
  const filtrate = useMemo(() => {
    const testo = ricerca.trim().toLocaleLowerCase("it");
    return righe.filter((r) => {
      if (soloNonEffettuati && !r.ritiro_non_effettuato) return false;
      if (filtroEsito && r.esito !== filtroEsito) return false;
      if (filtroTrasportatore === SENZA_TRASPORTATORE ? r.trasportatori.length > 0 : filtroTrasportatore && !r.trasportatori.some((t) => t.id === filtroTrasportatore))
        return false;
      if (!testo) return true;
      return [r.nome_punto, r.codice_gdo, r.localita, r.provincia, r.id_macchina, ...r.clienti].some((v) =>
        v?.toLocaleLowerCase("it").includes(testo)
      );
    });
  }, [righe, filtroEsito, filtroTrasportatore, ricerca, soloNonEffettuati]);

  const filtriAttivi = filtroEsito !== "" || filtroTrasportatore !== "" || ricerca.trim() !== "" || soloNonEffettuati;

  // --- Testata -------------------------------------------------------------
  const conteggio = (e: EsitoRitiro) => situazione?.esiti.find((x) => x.esito === e)?.conteggio ?? null;
  const kpi: KpiRitiri[] = KPI.map((k) => {
    const n = situazione ? conteggio(k.esito) : null;
    return { etichetta: k.etichetta, valore: n, caldo: k.caldo && (n ?? 0) > 0 };
  });

  const pillole: PillolaRitiri[] = [];
  const nonEffettuati = situazione?.ritiri_non_effettuati ?? 0;
  if (situazione) {
    // In cima, prima di tutto: i ritiri richiesti che la vigilanza non ha fatto (v1.14).
    if (nonEffettuati > 0) {
      pillole.push({ testo: `${nonEffettuati} ${nonEffettuati === 1 ? "ritiro non effettuato" : "ritiri non effettuati"}`, allarme: true });
    }
    const base = situazione.calendario_base;
    pillole.push({
      testo: (
        <>
          <span aria-live="polite">
            Scadenza richieste: {giornoBreve(base.D)} ore 16:00 ·{" "}
            {mancano !== null && mancano > 0 ? <span className="font-plex">mancano {hhmm(mancano)}</span> : "scadenza passata, aggiornamento…"}
          </span>
          <AiutoRitiri testo={DATE.D.testo} etichetta="Aiuto: scadenza richieste" suScuro />
        </>
      ),
    });
    // Avviso oltre 24 ore: la stessa regola della pagina di import, in tempo
    // reale trascorso. Si rivaluta a ogni giro di `useAdesso` (che rifà il
    // render), quindi compare anche a pagina aperta, senza rileggere la
    // situazione; `dati_non_aggiornati` dell'API era fermo all'ultima lettura.
    const vecchi = datiVecchi(situazione.dati_aggiornati_al);
    pillole.push(
      situazione.dati_aggiornati_al
        ? {
            testo: `Dati aggiornati al ${formattaDataAznext(situazione.dati_aggiornati_al)}${vecchi ? " · più vecchi di 24 ore" : ""}`,
            allarme: vecchi,
          }
        : { testo: "Nessun dato AZNext importato", allarme: true }
    );
  }

  const azioni = (
    <>
      {/* Visibile a tutti quelli che vedono la pagina (ritiri:read). */}
      <button
        type="button"
        onClick={() => setFinestra("legenda")}
        disabled={!situazione}
        className="ritiri-lift inline-flex h-11 items-center gap-2 rounded-[11px] border border-white/30 bg-white/10 px-5 text-sm font-bold text-white hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
      >
        <BookOpen className="h-4 w-4" aria-hidden="true" />
        Legenda
      </button>
      {hasPermission("ritiri:import") && (
        <Link
          to="/ritiri-valori/import"
          className="ritiri-lift inline-flex h-11 items-center gap-2 rounded-[11px] bg-accent px-5 text-sm font-bold text-primary hover:bg-accent-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          Importa
        </Link>
      )}
    </>
  );

  const base = situazione?.calendario_base;

  return (
    <div>
      <TestataRitiri
        sopratitolo="Ritiri valori · situazione casseforti"
        titolo="Ritiri valori"
        sottotitolo={
          base ? (
            <>
              Richieste entro {giornoBreve(base.D)} alle 16:00 per il ritiro di {giornoBreve(base.P1)}; se salta, il ritiro successivo è{" "}
              {giornoBreve(base.P2)}. La pagina mostra i calcoli del motore e non invia nulla da sola.
            </>
          ) : (
            "Quali casseforti vanno svuotate e quando, dai dati AZNext importati."
          )
        }
        pillola={pillole}
        kpi={kpi}
        azioni={azioni}
      />
      <SchedeRitiri schede={SCHEDE.map((s) => ({ etichetta: s, attiva: s === scheda }))} onSeleziona={(s) => setScheda(s as Scheda)} />

      <div className="-mx-4 px-4 pb-10 pt-[22px] sm:-mx-6 sm:px-10">
        {errore && (
          <CardRitiri className="mb-6">
            <p className="text-sm text-ink" role="alert">
              {errore}
            </p>
            <PulsanteRitiri type="button" variante="secondario" className="mt-3 h-9 px-3.5" onClick={carica} inCorso={inCaricamento}>
              {!inCaricamento && <RefreshCw className="h-4 w-4" aria-hidden="true" />}
              Riprova
            </PulsanteRitiri>
          </CardRitiri>
        )}

        {!situazione && !errore && <p className="text-sm text-ritiri-th-testo">Caricamento della situazione…</p>}

        {situazione && nonEffettuati > 0 && scheda === "Situazione" && (
          <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-danger bg-danger/10 p-4 text-sm text-ink" role="alert" data-avviso="ritiri-non-effettuati">
            <AlertTriangle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
            <p className="min-w-0 flex-1">
              <strong>
                {nonEffettuati === 1 ? "1 ritiro richiesto non è stato effettuato" : `${nonEffettuati} ritiri richiesti non sono stati effettuati`}
              </strong>
              : i dati di quei giorni sono completi e il prelievo non c'è. Dal dettaglio si prepara il sollecito alla vigilanza.
            </p>
            <PulsanteRitiri
              type="button"
              variante="secondario"
              className="h-9 px-3.5"
              aria-pressed={soloNonEffettuati}
              onClick={() => setSoloNonEffettuati((v) => !v)}
            >
              {soloNonEffettuati ? "Mostra tutte" : "Mostra solo queste"}
            </PulsanteRitiri>
          </div>
        )}

        {situazione && scheda === "Regole" && <RegoleRitiri stagionalita={situazione.stagionalita} eventiAttivi={situazione.eventi_attivi} onEventiCambiati={carica} />}

        {situazione && scheda === "Richieste" && (
          <RichiesteRitiri
            onApri={(id) => {
              setScheda("Situazione");
              setSelezionata(id);
            }}
          />
        )}

        {situazione && scheda === "Situazione" && (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:items-start">
            <div className="min-w-0 space-y-4">
              <Filtri
                situazione={situazione}
                trasportatori={trasportatori}
                esito={filtroEsito}
                onEsito={setFiltroEsito}
                trasportatore={filtroTrasportatore}
                onTrasportatore={setFiltroTrasportatore}
                ricerca={ricerca}
                onRicerca={setRicerca}
              />
              <div className="flex items-center justify-between gap-3 text-xs text-ritiri-th-testo">
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span aria-live="polite">
                    {filtrate.length === righe.length ? `${righe.length} casseforti` : `${filtrate.length} casseforti su ${righe.length}`}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    Barre: Adesso <AiutoRitiri testo={BARRE.adesso} etichetta="Aiuto: Adesso" /> · A P2{" "}
                    <AiutoRitiri testo={BARRE.aP2} etichetta="Aiuto: A P2" />
                  </span>
                </span>
                <button
                  type="button"
                  onClick={carica}
                  disabled={inCaricamento}
                  className="inline-flex items-center gap-1 font-semibold hover:text-ink disabled:opacity-50"
                >
                  <RefreshCw className={clsx("h-3.5 w-3.5", inCaricamento && "animate-spin motion-reduce:animate-none")} aria-hidden="true" />
                  Aggiorna
                </button>
              </div>

              {righe.length === 0 ? (
                <CardRitiri>
                  <p className="text-sm text-ink">
                    Nessuna cassaforte: non sono ancora stati importati dati AZNext.
                    {hasPermission("ritiri:import") && (
                      <>
                        {" "}
                        <Link to="/ritiri-valori/import" className="font-semibold underline">
                          Vai all'import
                        </Link>
                        .
                      </>
                    )}
                  </p>
                </CardRitiri>
              ) : filtrate.length === 0 ? (
                <CardRitiri>
                  <p className="text-sm text-ink">Nessuna cassaforte corrisponde ai filtri.</p>
                  {filtriAttivi && (
                    <PulsanteRitiri
                      type="button"
                      variante="secondario"
                      className="mt-3 h-9 px-3.5"
                      onClick={() => {
                        setFiltroEsito("");
                        setFiltroTrasportatore("");
                        setRicerca("");
                        setSoloNonEffettuati(false);
                      }}
                    >
                      Togli i filtri
                    </PulsanteRitiri>
                  )}
                </CardRitiri>
              ) : (
                <ul className="space-y-2.5" aria-label="Casseforti">
                  {filtrate.map((r, i) => (
                    <RigaCassaforte key={r.id_macchina} r={r} indice={i} selezionata={r.id_macchina === selezionata} onApri={() => setSelezionata(r.id_macchina)} />
                  ))}
                </ul>
              )}
            </div>

            {/* Dettaglio: colonna a destra sullo schermo largo. Sotto `lg` è la finestra modale qui sotto. */}
            {largo && (
              <aside aria-label="Dettaglio cassaforte" className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
                {selezionata ? (
                  <DettaglioCassaforte
                    idMacchina={selezionata}
                    versione={versione}
                    onChiudi={chiudiDettaglio}
                    onImpostazioni={apriImpostazioni}
                    onCambiata={carica}
                  />
                ) : (
                  <CardRitiri>
                    <p className="text-sm text-ritiri-th-testo">Seleziona una cassaforte per vedere motivazioni, dati e grafico.</p>
                  </CardRitiri>
                )}
              </aside>
            )}
          </div>
        )}
      </div>

      {modale && (
        <FinestraRitiri idTitolo={ID_TITOLO_DETTAGLIO} variante="pagina" onChiudi={chiudiDettaglio}>
          <DettaglioCassaforte
            idMacchina={selezionata}
            versione={versione}
            onChiudi={chiudiDettaglio}
            onImpostazioni={apriImpostazioni}
            onCambiata={carica}
          />
        </FinestraRitiri>
      )}
      {finestra === "impostazioni" && selezionata && situazione && (
        <FinestraRitiri idTitolo={ID_TITOLO_IMPOSTAZIONI} variante={largo ? "pannello" : "pagina"} onChiudi={chiudiFinestra}>
          <ImpostazioniCassaforte
            idMacchina={selezionata}
            categorie={Object.keys(situazione.stagionalita)}
            onChiudi={chiudiFinestra}
            onSalvate={() => {
              chiudiFinestra();
              carica();
            }}
          />
        </FinestraRitiri>
      )}
      {finestra === "legenda" && situazione && (
        <FinestraRitiri idTitolo={ID_TITOLO_LEGENDA} variante={largo ? "pannello" : "pagina"} onChiudi={chiudiFinestra}>
          <LegendaRitiri base={situazione.calendario_base} onChiudi={chiudiFinestra} />
        </FinestraRitiri>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Filtri
// ----------------------------------------------------------------------------

const controllo =
  "h-10 w-full rounded-[11px] border border-ritiri-bordo-forte bg-white px-3 text-sm text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

function Filtri(p: {
  situazione: SituazioneRitiri;
  trasportatori: [string, string][];
  esito: EsitoRitiro | "";
  onEsito: (e: EsitoRitiro | "") => void;
  trasportatore: string;
  onTrasportatore: (t: string) => void;
  ricerca: string;
  onRicerca: (t: string) => void;
}) {
  return (
    <div className="grid gap-3 rounded-2xl border border-ritiri-bordo bg-white p-4 sm:grid-cols-3">
      <label className="block text-xs font-bold text-ritiri-th-testo">
        Esito
        <select className={clsx(controllo, "mt-1")} value={p.esito} onChange={(e) => p.onEsito(e.target.value as EsitoRitiro | "")}>
          <option value="">Tutti gli esiti</option>
          {p.situazione.esiti.map((e) => (
            <option key={e.esito} value={e.esito}>
              {e.nome} ({e.conteggio})
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs font-bold text-ritiri-th-testo">
        Trasportatore
        <select className={clsx(controllo, "mt-1")} value={p.trasportatore} onChange={(e) => p.onTrasportatore(e.target.value)}>
          <option value="">Tutti i trasportatori</option>
          {p.trasportatori.map(([id, nome]) => (
            <option key={id} value={id}>
              {nome}
            </option>
          ))}
          <option value={SENZA_TRASPORTATORE}>Senza trasportatore</option>
        </select>
      </label>
      <label className="block text-xs font-bold text-ritiri-th-testo">
        Cerca
        <span className="relative mt-1 block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ritiri-th-testo" aria-hidden="true" />
          <input
            type="search"
            className={clsx(controllo, "pl-9")}
            placeholder="Punto, GDO, località, codice…"
            value={p.ricerca}
            onChange={(e) => p.onRicerca(e.target.value)}
          />
        </span>
      </label>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Riga dell'elenco (una scheda su telefono, una riga a colonne sullo schermo largo)
// ----------------------------------------------------------------------------

function RigaCassaforte({ r, indice, selezionata, onApri }: { r: RigaSituazioneRitiri; indice: number; selezionata: boolean; onApri: () => void }) {
  const localita = [r.localita, r.provincia ? `(${r.provincia})` : null].filter(Boolean).join(" ");
  // Comparsa a cascata solo per le prime righe: oltre, il ritardo diventerebbe attesa.
  const stile: CSSProperties = { animationDelay: `${Math.min(indice, 12) * 40}ms` };
  // La scheda ha tre tipi di controlli affiancati, mai uno dentro l'altro: il
  // pulsante della riga (apre il dettaglio), il badge e i chip (aprono il loro
  // aiuto). Il badge sta sopra l'angolo della scheda, fuori dal pulsante.
  return (
    <li
      className={clsx(
        "ritiri-rise ritiri-riga relative rounded-2xl border bg-white shadow-[0_1px_2px_rgba(24,39,61,.05)] hover:bg-ritiri-hover",
        selezionata ? "border-accent ring-1 ring-accent" : "border-ritiri-bordo"
      )}
      style={stile}
      data-id-macchina={r.id_macchina}
    >
      <button
        type="button"
        onClick={onApri}
        aria-pressed={selezionata}
        className={clsx(
          "block w-full rounded-2xl p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
          r.etichette.length > 0 && "pb-2"
        )}
      >
        <span className="block min-w-0 pr-36">
          <span className="nome-punto block break-words font-bold text-ink">{r.nome_punto}</span>
          {r.ritiro_non_effettuato && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-danger px-2 py-0.5 text-[11px] font-bold text-white" data-badge="non-effettuato">
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              Ritiro non effettuato
            </span>
          )}
          <span className="mt-0.5 block text-xs text-ritiri-th-testo">
            {[r.codice_gdo ? `GDO ${r.codice_gdo}` : "GDO —", localita || null].filter(Boolean).join(" · ")}
            <span className="ml-1.5 font-plex">{r.id_macchina}</span>
          </span>
          {r.calendario_diverso && (
            <span className="mt-0.5 block text-[11px] font-semibold text-ritiri-th-testo" title="Calendario del trasportatore diverso da quello base">
              P1 {giornoBreve(r.P1)}
            </span>
          )}
        </span>
        <span className="mt-3 block space-y-2">
          <BarraRiempimento etichetta="Adesso" punto={r.giacenza} />
          <BarraRiempimento etichetta="A P2" punto={r.previsto_p2} stima />
        </span>
      </button>
      <span className="absolute right-4 top-4">
        <BadgeEsito esito={r.esito} testo={r.esito_testo} conAiuto />
      </span>
      {r.etichette.length > 0 && (
        <div className="px-4 pb-4 pt-1">
          <ChipEtichette etichette={r.etichette} conAiuto />
        </div>
      )}
    </li>
  );
}
